<template>
  <div>
    <div class="cell border-bottom small-padding-bottom-m large-padding-bottom-l">
      <h1>Return a gift</h1>
    </div>
    <div class="grid-x">
      <div
        class="cell grid-x small-12 small-order-1 small-margin-top-m large-margin-top-l large-padding-right-z"
        :class="{
          'medium-7': isMcom && !isSignedInAndLoyalty,
          'medium-6 large-8 mlarge-7': isMcom,
          'medium-12 large-12': isBcom,
        }"
      >
        <Notification
          v-if="orderDetailsStore.orderDetailsError || showLockoutMessage"
          class="cell margin-top-z small-margin-bottom-m large-margin-bottom-l"
          severity="error"
          :closable="false"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="gift-return-error" v-html="notificationMessage"></div>
        </Notification>
        <Form
          id="gift-return-search-form"
          v-slot="{ setErrors }"
          class="cell"
          :class="{
            'medium-9': isMcom && !isSignedInAndLoyalty,
            'small-12 large-6 mlarge-5': isMcom,
            'medium-5 large-3': isBcom,
          }"
          @submit="onSubmit"
        >
          <div class="margin-bottom-m">
            <InputText
              v-model="form.orderNumber"
              name="orderNumber"
              label="Order number"
              :rule="validateOrderNumber"
              :disabled="orderDetailsStore.isLoading"
              @keypress="digitsOnly($event)"
              @input="resetValidationState"
            />
          </div>
          <AppButton id="gift-return-submit" class="primary expanded" type="submit" :disabled="disableSubmit" :loading="isLoading">
            Search
          </AppButton>
          <button ref="resetFormValidation" type="button" hidden @click.prevent="setErrors([])">Reset</button>
        </Form>
      </div>
      <!-- eslint-disable next-block vue/no-v-html -->
      <!-- prettier-ignore -->
      <div
        class="large cell padding-right-xs 
        small-12 small-order-3 small-margin-top-m 
        medium-padding-bottom-xxxl medium-margin-bottom-xxxl 
        large-margin-top-l"
        :class="{
          'medium-7': isMcom && !isSignedInAndLoyalty,
          'medium-6 large-6': isMcom,
        }"
        v-html="config.GIFT_RETURN.contact"
      ></div>
      <div
        v-if="isMcom && isSignedInAndLoyalty"
        class="banner-column small-12 small-order-2 small-margin-top-m medium-6 large-4 large-margin-top-l mlarge-offset-1"
      >
        <div class="banner padding-m medium-margin-left-s large-margin-left-z">
          <h3>Get free return shipping <br />when you join Star Rewards</h3>
          <div class="margin-top-s">
            <a id="gift-return-join-now" href="#" @click.prevent="joinNow">Join now</a>
          </div>
          <div class="star-rewards-logo grid-x align-right small-margin-top-xxl">
            <img :src="StarRewardImg" alt="Macy's Star Reward Logo" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';

import { UserStatus, Cookie } from '@nuxt-common/util/app/utils/cookie';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification';
import InputText from '@nuxt-common/atomic-ui/app/components/inputText/InputText';
import { veevalidate } from '@nuxt-common/util/app/utils/forms';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';

import { orderLookupFailedAttemptsConfig } from '@/utils/OrderLookupFailedAttempts';
import { digitsOnly, validateOrderNumber } from '@/utils/General';
import AppButton from '@/components/AppButton';
import useBrand from '@/composables/useBrand';

import * as config from 'config';

// Pinia Store & Composables
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import { useJoinStarRewardsStore } from '@/stores/useJoinStarRewardsStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';

import StarRewardImg from '@core/csg/src/assets/images/mcom/compressed/star-rewards-large.svg';

const { Form } = veevalidate;
const orderDetailsStore = useOrderDetailsStore();
const starRewardStore = useJoinStarRewardsStore();
const envPropsStore = useEnvPropsStore();

const { isMcom, isBcom } = useBrand();

// Reactive state
const showLockoutMessage = ref(false);
const form = ref({ orderNumber: '' });
const resetFormValidation = ref(null);

// Computed properties
const disableSubmit = computed(
  () =>
    !form.value.orderNumber ||
    orderDetailsStore.isLoading ||
    showLockoutMessage.value ||
    orderDetailsStore.orderDetailsError === orderLookupFailedAttemptsConfig.ORDER_LOOKUP_FAILED_ATTEMPTS_MSG,
);
const isSignedInAndLoyalty = computed(() => {
  if (starRewardStore.starRewardsSummary.loading) return false;
  return !UserStatus.isSignedIn() && starRewardStore.starRewardsSummary.data?.encryptedLoyaltyId;
});
const notificationMessage = computed(() => orderDetailsStore.orderDetailsError || orderLookupFailedAttemptsConfig.ORDER_LOOKUP_FAILED_ATTEMPTS_MSG);

const resetValidationState = () => {
  resetFormValidation.value.click();
};

const onSubmit = async (values) => {
  fireTag('link', {
    event_name: 'gift return order search',
    custom_link_name: 'gift return_search',
  });
  await orderDetailsStore.GET_ONLINE_ORDER_DETAILS({
    payload: {
      orderNumber: values.orderNumber,
      fromLookup: 'returns',
      onlyOrderNumber: true,
      isSignedIn: UserStatus.isSignedIn(),
    },
    router: window.location,
  });
};

const joinNow = () => {
  fireTag('link', {
    event_name: 'join now',
    link_name: 'gift return_lty_join now',
  });
  window.location.href = 'https://www.macys.com/account/createaccount?cm_sp=my_account-_-sign_in-_-create_account';
};

onMounted(async () => {
  fireTag('view', {
    page_name: 'gift return',
    page_type: 'return center',
  });
  if (!UserStatus.isSignedIn()) {
    const domain = envPropsStore.envProps.cookieDomain;
    Cookie.set('FORWARDPAGE_KEY', `${window.location.origin}/returns/gifts`, null, {
      domain,
    });

    window.location.href = config.RETURN_CENTER_TEXT.giftReturn.url;
  }
  if (orderLookupFailedAttemptsConfig.isOrderLookupFailedAttempts()) {
    showLockoutMessage.value = true;
  }
  if (isMcom) {
    await starRewardStore.FETCH_STAR_REWARDS_SUMMARY(false);
  }
});
</script>
<style lang="scss" scoped>
.banner-column {
  position: relative;
  .banner {
    position: absolute;
    right: 0;
    max-width: 100%;
    background: $loyalty-bg-3-color;
    @include breakpoint(small down) {
      position: relative;
    }
    .star-rewards-logo {
      @include breakpoint(medium up) {
        margin-top: rem-calc(200) !important;
      }
      img {
        max-width: rem-calc(310px);
        width: 100%;
        @include breakpoint(small down) {
          max-width: rem-calc(190px);
        }
      }
    }
  }
}
</style>
